import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime,
  AttachMoney,
  MoreVert,
  Person,
  EventNote,
  Phone,
  CalendarToday,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import AppointmentStatusBadge from './AppointmentStatusBadge';
import AppointmentDetailsModal from './AppointmentDetailsModal';
import { appointmentService } from '../services/appointmentService';

interface AppointmentCardProps {
  appointment: any;
  userRole?: 'PATIENT' | 'CAREGIVER' | 'ADMIN' | null;
  onAction: (accepted?: boolean) => void;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({ appointment, userRole, onAction }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatPrice = (price?: number | string | null) => {
    if (!price) return 'Not set';
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numericPrice)) return 'Not set';
    return `$${numericPrice.toFixed(2)}`;
  };

  const getTimeUntil = (dateString: string) => {
    const appointmentDate = new Date(dateString);
    const now = new Date();
    const diffMs = appointmentDate.getTime() - now.getTime();
    
    if (diffMs < 0) return null;
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffHours < 24) {
      if (diffHours < 1) return 'In less than 1 hour';
      if (diffHours === 1) return 'In 1 hour';
      return `In ${diffHours} hours`;
    }
    
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `In ${diffDays} days`;
    
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks === 1) return 'In 1 week';
    return `In ${diffWeeks} weeks`;
  };

  const isPatient = userRole === 'PATIENT';
  const isCaregiver = userRole === 'CAREGIVER';
  const isPending = appointment.status === 'REQUESTED';
  const otherParty = isPatient ? appointment.caretaker : appointment.patient;
  const timeUntil = getTimeUntil(appointment.scheduledAt);

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to accept this appointment request?')) {
      return;
    }

    try {
      setAccepting(true);
      await appointmentService.updateAppointment(appointment.id, { status: 'CONFIRMED' });
      onAction(true); // Pass true to indicate acceptance
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to accept appointment');
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to reject this appointment request?')) {
      return;
    }

    try {
      setRejecting(true);
      await appointmentService.updateAppointment(appointment.id, { status: 'CANCELLED' });
      onAction(false); // Pass false to indicate rejection
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to reject appointment');
    } finally {
      setRejecting(false);
    }
  };

  return (
    <>
      <Card 
        sx={{ 
          cursor: 'pointer',
          '&:hover': { boxShadow: 3 },
          transition: 'box-shadow 0.2s',
        }}
        onClick={() => setDetailsOpen(true)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={otherParty?.avatar}
                sx={{ width: 56, height: 56 }}
              >
                {otherParty?.firstName?.[0]}{otherParty?.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {otherParty?.firstName} {otherParty?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isPatient ? 'Caregiver' : 'Careseeker'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AppointmentStatusBadge status={appointment.status} />
              {timeUntil && (
                <Chip 
                  label={timeUntil} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventNote fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  Service:
                </Typography>
                <Typography variant="body2">
                  {appointment.service?.name || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarToday fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  Date:
                </Typography>
                <Typography variant="body2">
                  {formatDate(appointment.scheduledAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  Time:
                </Typography>
                <Typography variant="body2">
                  {formatTime(appointment.scheduledAt)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2" fontWeight="medium">
                  Duration:
                </Typography>
                <Typography variant="body2">
                  {appointment.duration} minutes
                </Typography>
              </Box>
              {appointment.totalAmount != null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AttachMoney fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium">
                    Price:
                  </Typography>
                  <Typography variant="body2">
                    {formatPrice(appointment.totalAmount)}
                  </Typography>
                </Box>
              )}
              {!isPatient && appointment.paymentStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight="medium">
                    Payment:
                  </Typography>
                  <Chip 
                    label={appointment.paymentStatus} 
                    size="small"
                    color={appointment.paymentStatus === 'PAID' ? 'success' : 'warning'}
                  />
                </Box>
              )}
            </Grid>
          </Grid>

          {appointment.notes && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Notes:
              </Typography>
              <Typography variant="body2">
                {appointment.notes}
              </Typography>
            </Box>
          )}

          {appointment.specialRequirements && !isPatient && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
              <Typography variant="caption" color="warning.dark" fontWeight="medium">
                Special Requirements:
              </Typography>
              <Typography variant="body2" color="warning.dark">
                {appointment.specialRequirements}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {isCaregiver && isPending && (
              <>
                <Button 
                  size="small" 
                  variant="contained"
                  color="success"
                  startIcon={accepting ? <CircularProgress size={16} /> : <CheckCircle />}
                  onClick={handleAccept}
                  disabled={accepting || rejecting}
                >
                  {accepting ? 'Accepting...' : 'Accept'}
                </Button>
                <Button 
                  size="small" 
                  variant="outlined"
                  color="error"
                  startIcon={rejecting ? <CircularProgress size={16} /> : <Cancel />}
                  onClick={handleReject}
                  disabled={accepting || rejecting}
                >
                  {rejecting ? 'Rejecting...' : 'Reject'}
                </Button>
              </>
            )}
            <Button 
              size="small" 
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setDetailsOpen(true);
              }}
            >
              View Details
            </Button>
          </Box>
        </CardContent>
      </Card>

      <AppointmentDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        appointment={appointment}
        userRole={userRole}
        onAction={onAction}
      />
    </>
  );
};

export default AppointmentCard;

